/* eslint-disable no-restricted-imports */
import { useMemo, useState } from 'react';
import { PostModel } from '../../../data-layer/posts/types';
import { withPostCallbacks } from '../../../routing/post/withPostCallbacks';
import { withAuthProtection } from '../../../routing/withAuthProtection';
import { useEventSource } from '../../../utils/LocationContext';
import { isBloggerBusiness } from '../../../utils/accessService';
import { EventSource } from '../../../utils/analyticsService';
import { cropPublicId } from '../../../utils/cropPublicId';
import { formatText } from '../../../utils/format/formatText';
import { getDocument } from '../../../utils/getDocument';
import { useIsMobile } from '../../../utils/getIsMobile';
import { CarouselIndicators } from '../../carousel/CarouselIndicators';
import { Dialog, DialogContent, DialogFooter } from '../../feedback/Dialog';
import { useDialogScroll } from '../../feedback/hooks/useDialogScroll';
import { IconCross } from '../../icon/IconCross';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Avatar } from '../../media/Avatar';
import {
    PostDetailsCardContext,
    PostDetailsCardContextPayload,
} from '../../posts/PostDetailsCard/PostDetailsCard';
import { PostCarousel } from '../../posts/PostDetailsCard/parts/PostCarousel';
import { getCarouselSizes } from '../../posts/PostDetailsCard/utils';
import { Typography } from '../../typography/Typography';
import { ActionButton } from '../styles';
import { CloseButton, IndicatorsContainer } from './styles';

const MODAL_WIDTH = 512;
const FOOTER_HEIGHT = 74;
const TEXT_HEIGHT = 74;

interface PostCollectionPreviewProps {
    post: PostModel;
    submitButtonText: string;
    onClose: VoidFunction;
    onSubmit: VoidFunction;
}

export const PostCollectionPreview = withAuthProtection<PostCollectionPreviewProps>(
    ({ post, onClose, onSubmit, submitButtonText }) => {
        const { eventSource } = useEventSource();
        const [soundControlContainer, setSoundControlContainer] = useState<HTMLDivElement | null>(
            null,
        );
        const [isProductExpanded, setIsProductExpanded] = useState<boolean>(false);
        const [activeIndex, setActiveIndex] = useState<number>(0);
        const isMobile = useIsMobile();

        const { modalContentRef, atBottom } = useDialogScroll();

        const docHeight = getDocument()?.documentElement.clientHeight;

        const businessName = isBloggerBusiness(post.business?.type)
            ? post.business?.displayName
            : cropPublicId(post.business?.publicId || '');

        const displayName = businessName || post.ownersData?.author?.displayName;

        const carouselMaxHeight = docHeight
            ? docHeight * 0.9 - FOOTER_HEIGHT - TEXT_HEIGHT
            : undefined;

        const sizes = useMemo(
            () =>
                getCarouselSizes(post, {
                    horizontalText: false,
                    maxHeight: carouselMaxHeight,
                    maxWidth: MODAL_WIDTH,
                }),
            [carouselMaxHeight, post],
        );

        const EnhancedPostCarousel = useMemo(
            () => withPostCallbacks(PostCarousel, eventSource ?? EventSource.MULTICOLUMN),
            [eventSource],
        );

        const contextValue: PostDetailsCardContextPayload = {
            soundControlContainer,
            isProductExpanded,
            activeIndex,
            sizes,
            setSoundControlContainer,
            setIsProductExpanded,
            setActiveIndex,
            setSizes: () => {},
        };

        const soundControlContainerEnabled = post.content[activeIndex]?.type === 'video';

        if (!post) {
            return null;
        }

        return (
            <Dialog preventScroll={false} maxWidth={MODAL_WIDTH} height="auto" onClose={onClose}>
                <CloseButton variant="blur" color="secondary" onClick={onClose}>
                    <IconCross size={24} />
                </CloseButton>
                <DialogContent ref={modalContentRef} py={0} px={0} height="100%">
                    {soundControlContainerEnabled ? (
                        <Box
                            position="absolute"
                            top={7.5}
                            right={1}
                            zIndex={5}
                            dataTestId={DataTestId.MUTE}
                            ref={(element) => {
                                setSoundControlContainer(element);
                            }}
                        />
                    ) : null}
                    {isMobile ? (
                        <IndicatorsContainer>
                            <CarouselIndicators
                                variant="story"
                                activeIndex={activeIndex}
                                count={post.content.length}
                            />
                        </IndicatorsContainer>
                    ) : null}
                    <PostDetailsCardContext.Provider value={contextValue}>
                        <Flex
                            direction="row"
                            justifyContent="center"
                            width="100%"
                            backgroundColor="black"
                        >
                            <EnhancedPostCarousel post={post} index={0} resizeDisabled />
                        </Flex>
                    </PostDetailsCardContext.Provider>
                    {post.business ? (
                        <Flex direction="row" py={1} px={2}>
                            <Avatar
                                width={24}
                                title={displayName}
                                src={post.business.avatarUrl}
                                border="1px solid var(--divider)"
                                mr={1}
                            />
                            <Typography alignSelf="center" variant="caption" weight="medium">
                                {displayName}
                            </Typography>
                        </Flex>
                    ) : null}
                    <Typography variant="body2" px={2} py={1}>
                        {formatText(post.text)}
                    </Typography>
                </DialogContent>
                <DialogFooter withBorderTop={!atBottom}>
                    <Box
                        width="100%"
                        height="100%"
                        backgroundColor="var(--background-primary-blur)"
                    >
                        <ActionButton onClick={onSubmit} color="primary">
                            {submitButtonText}
                        </ActionButton>
                    </Box>
                </DialogFooter>
            </Dialog>
        );
    },
);
